import { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Stethoscope, Eye, EyeOff, ShieldCheck, Mail, Clock, CheckCircle2 } from "lucide-react";

export default function DoctorSignup() {
    const [form, setForm] = useState({
        first_name: "", last_name: "", email: "", password: "", confirm: "",
        speciality: "General Practitioner", license_number: "", hospital: "", bio: "",
        country_code: "+27", mobile: "",
    });
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    // Iteration 10 — show the review screen instead of auto-login
    const [submitted, setSubmitted] = useState(null); // {email, message}

    const submit = async (e) => {
        e.preventDefault();
        if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
        if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/doctors/register", {
                first_name: form.first_name, last_name: form.last_name,
                email: form.email, password: form.password,
                speciality: form.speciality, license_number: form.license_number,
                hospital: form.hospital, bio: form.bio,
                country_code: form.country_code, mobile: form.mobile,
            });
            // Application pending — DO NOT auto-login; show reviewer-pending screen.
            setSubmitted({
                email: form.email,
                message: data?.message || "Your application is now being reviewed.",
            });
            toast.success("Application submitted for review");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="bg-white min-h-screen" data-testid="doctor-signup-review-screen">
                <Navbar />
                <main className="pt-28 pb-20">
                    <section className="max-w-xl mx-auto px-6 text-center">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[var(--medx-orange)] to-[var(--medx-red)] grid place-items-center mx-auto mb-6 text-white shadow-xl">
                            <Clock className="w-9 h-9" />
                        </div>
                        <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3">APPLICATION RECEIVED</div>
                        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mb-5">
                            Your application is <span className="brand-gradient-text">being reviewed.</span>
                        </h1>
                        <p className="text-gray-600 leading-relaxed mb-7" data-testid="doctor-signup-review-message">
                            {submitted.message}
                        </p>
                        <div className="card-soft p-6 text-left space-y-3 text-sm">
                            <Row icon={CheckCircle2} tone="green" label="Credentials captured" value="We have your registration details on file." />
                            <Row icon={Clock} tone="orange" label="Clinical operations review" value="Usually within one business day." />
                            <Row icon={Mail} tone="red" label="Email confirmation" value={<>We'll email <span className="font-mono font-semibold text-gray-900" data-testid="doctor-signup-review-email">{submitted.email}</span> as soon as you're approved.</>} />
                        </div>
                        <div className="mt-7 flex items-center justify-center gap-5 text-sm">
                            <Link to="/" className="text-[var(--medx-red)] font-semibold hover:underline" data-testid="doctor-signup-review-home">← Return to home</Link>
                            <span className="text-gray-300">·</span>
                            <Link to="/contact" className="text-gray-600 hover:text-[var(--medx-red)]">Contact us</Link>
                        </div>
                    </section>
                </main>
            </div>
        );
    }

    return (
        <div className="bg-white min-h-screen" data-testid="doctor-signup-page">
            <Navbar />
            <main className="pt-28 pb-20">
                <section className="max-w-xl mx-auto px-6">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3 text-center">JOIN AS A DOCTOR</div>
                    <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-center mb-3">
                        Treat patients <span className="brand-gradient-text">remotely.</span>
                    </h1>
                    <p className="text-center text-sm text-gray-500 mb-3">For licensed medical practitioners only.</p>
                    <div className="rounded-xl border border-orange-200 bg-orange-50/70 px-4 py-3 text-xs text-gray-700 mb-7 text-center" data-testid="doctor-signup-review-notice">
                        <ShieldCheck className="w-4 h-4 inline-block text-[var(--medx-red)] mr-1 -mt-0.5" />
                        All registrations go through a clinical-ops review. You will <strong>not</strong> be able to log in until your account is approved by email.
                    </div>

                    <div className="card-soft p-7">
                        <form onSubmit={submit} className="space-y-4" data-testid="doctor-signup-form">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input required type="text" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                                    className="px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm" data-testid="doctor-first-name" />
                                <input required type="text" placeholder="Surname" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                                    className="px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm" data-testid="doctor-last-name" />
                            </div>
                            <input required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm" data-testid="doctor-email" />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <select value={form.speciality} onChange={(e) => setForm({ ...form, speciality: e.target.value })}
                                    className="px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm bg-white" data-testid="doctor-speciality">
                                    {["General Practitioner", "Cardiologist", "Pediatrician", "Dermatologist", "Neurologist", "Psychiatrist", "Endocrinologist", "Other"].map((s) => (
                                        <option key={s}>{s}</option>
                                    ))}
                                </select>
                                <input required type="text" placeholder="License number *" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                                    className="px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm" data-testid="doctor-license" />
                            </div>
                            <input type="text" placeholder="Hospital / Practice name" value={form.hospital} onChange={(e) => setForm({ ...form, hospital: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm" data-testid="doctor-hospital" />
                            <textarea rows={3} placeholder="Short bio (optional)" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm resize-none" data-testid="doctor-bio" />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="relative">
                                    <input required type={show ? "text" : "password"} placeholder="Password (6+)" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm" data-testid="doctor-password" />
                                    <button type="button" onClick={() => setShow((v) => !v)} className="absolute inset-y-0 right-3 grid place-items-center text-gray-400">
                                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                <input required type={show ? "text" : "password"} placeholder="Confirm password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                                    className="px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm" data-testid="doctor-confirm" />
                            </div>
                            <div className="flex items-start gap-2 text-xs text-gray-500">
                                <ShieldCheck className="w-4 h-4 text-[var(--medx-orange)] mt-0.5 shrink-0" />
                                Your license number is encrypted in transit. Medx AI verifies all practitioners before activating telemedicine privileges.
                            </div>
                            <button type="submit" disabled={loading} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="doctor-signup-submit">
                                <Stethoscope className="w-4 h-4" /> {loading ? "Submitting application…" : "Submit application for review"}
                            </button>
                            <div className="text-center text-sm text-gray-500">
                                Already approved? <Link to="/doctor/login" className="text-[var(--medx-red)] font-semibold hover:underline">Doctor Login</Link>
                            </div>
                        </form>
                    </div>
                </section>
            </main>
        </div>
    );
}

function Row({ icon: Icon, tone, label, value }) {
    const toneColor = tone === "green" ? "text-emerald-600" : tone === "orange" ? "text-[var(--medx-orange)]" : "text-[var(--medx-red)]";
    return (
        <div className="flex items-start gap-3">
            <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${toneColor}`} />
            <div>
                <div className="text-xs font-mono tracking-[0.18em] text-gray-500">{label}</div>
                <div className="text-sm text-gray-800">{value}</div>
            </div>
        </div>
    );
}
